import type { SessionLog } from "../game/log";
import type { SpeechRecognizer } from "./types";

// Голос Кукушки. Полудуплекс: пока Кукушка говорит, распознавание заглушено
// (recognizer.mute()), после — защитное окно в unmute().
//
// Фиксированные реплики и эхо чисел проигрываются локально из
// /voice/ru/<id>.mp3 (сгенерированы `npm run voice:gen`). Если файла нет —
// фолбэк на /api/tts (server-side ElevenLabs, Voice ID на сервере).
// Обратное эхо ("ук-ук") — реверс AudioBuffer, синтез задом наперёд невозможен.

const LINE_IDS = [
  "hush", "raz", "more", "now_you_two", "cuckoo_night", "want_frog", "wow_back",
  "dizzy", "giggle", "only_tomorrow", "egg_cold", "too_early", "mom_at_eight", "louder",
] as const;
export type CuckooLineId = (typeof LINE_IDS)[number];

const MIN_GAP_MS = 20_000; // не чаще одной реплики в 20 с (эхо — исключение)

export class CuckooVoice {
  private ac: AudioContext | null = null;
  private cache = new Map<string, AudioBuffer | null>();
  private lastLineAt = 0;
  private current: AudioBufferSourceNode | null = null;
  private speaking = false;

  constructor(
    private recognizer: { mute(): void; unmute(): void } | SpeechRecognizer,
    private log: SessionLog,
    private onPhase: (speaking: boolean) => void,
  ) {}

  /** Разблокировать AudioContext из user-gesture (касание дверцы). */
  unlock() {
    if (!this.ac) {
      const AC: typeof AudioContext = (window as any).AudioContext || (window as any).webkitAudioContext;
      this.ac = new AC();
    }
    if (this.ac.state === "suspended") void this.ac.resume();
  }

  get isSpeaking() {
    return this.speaking;
  }

  private async load(url: string): Promise<AudioBuffer | null> {
    if (this.cache.has(url)) return this.cache.get(url)!;
    if (!this.ac) this.unlock();
    try {
      const r = await fetch(url);
      if (!r.ok) throw new Error(String(r.status));
      const arr = await r.arrayBuffer();
      const buf = await this.ac!.decodeAudioData(arr);
      this.cache.set(url, buf);
      return buf;
    } catch {
      this.cache.set(url, null);
      return null;
    }
  }

  private reversed(buf: AudioBuffer): AudioBuffer {
    const out = this.ac!.createBuffer(buf.numberOfChannels, buf.length, buf.sampleRate);
    for (let c = 0; c < buf.numberOfChannels; c++) {
      const src = buf.getChannelData(c);
      const dst = out.getChannelData(c);
      for (let i = 0, j = src.length - 1; i < src.length; i++, j--) dst[i] = src[j];
    }
    return out;
  }

  private play(buf: AudioBuffer): Promise<void> {
    return new Promise((resolve) => {
      if (!this.ac) return resolve();
      const node = this.ac.createBufferSource();
      node.buffer = buf;
      node.connect(this.ac.destination);
      this.current = node;
      node.onended = () => {
        this.current = null;
        resolve();
      };
      node.start();
    });
  }

  private beginSpeak() {
    this.speaking = true;
    this.recognizer.mute();
    this.onPhase(true);
    this.log.add("tts_start");
  }

  private endSpeak() {
    this.speaking = false;
    this.recognizer.unmute(); // добавляет защитное окно
    this.onPhase(false);
    this.log.add("tts_end");
  }

  private async resolveBuffer(id: string, text: string): Promise<AudioBuffer | null> {
    const local = await this.load(`/voice/ru/${id}.mp3`);
    if (local) return local;
    // Фолбэк: рантайм-синтез через сервер (Voice ID остаётся на сервере).
    const url = `/api/tts?text=${encodeURIComponent(text)}`;
    return this.load(url);
  }

  /** Сказать фиксированную реплику. `force` обходит лимит 20 с. */
  async say(id: CuckooLineId, text: string, opts: { force?: boolean } = {}): Promise<void> {
    const now = performance.now();
    if (!opts.force && now - this.lastLineAt < MIN_GAP_MS) return;
    this.lastLineAt = now;
    const buf = await this.resolveBuffer(id, text);
    if (!buf) {
      this.log.add("tts_error", { id });
      return;
    }
    this.beginSpeak();
    this.log.add("character_event", { who: "cuckoo", line: id });
    await this.play(buf);
    this.endSpeak();
  }

  /** Эхо числа 0..10. reversed — для обратного счёта. Лимит 20 с не действует. */
  async echo(n: number, reversed = false): Promise<void> {
    if (n < 0 || n > 10) return;
    // При быстром счёте эхо не наслаивается: если Кукушка ещё говорит — пропускаем.
    if (this.speaking) return;
    const words = ["ноль", "раз", "два", "три", "четыре", "пять", "шесть", "семь", "восемь", "девять", "десять"];
    const buf = await this.resolveBuffer(`echo_${n}`, words[n]);
    if (!buf) return;
    this.beginSpeak();
    await this.play(reversed ? this.reversed(buf) : buf);
    this.endSpeak();
  }

  /** Двойное эхо при пропуске ровно одного числа: «шесть, семь». */
  async echoPair(a: number, b: number): Promise<void> {
    await this.echo(a);
    await this.echo(b);
  }

  stopAll() {
    try {
      this.current?.stop();
    } catch {
      /* noop */
    }
    if (this.speaking) this.endSpeak();
  }
}
